<template>
  <div class="page" v-if="campaign">
    <div class="page-header">
      <h1 class="page-title">Campaign</h1>
      <breadcrumb
        name="WSGT_DRIVE_PROGRESS_UPLOAD"
        :withLast.sync="campaign.name"
      ></breadcrumb>
      <div class="page-header-actions">
        <button
          type="button"
          class="btn btn-round btn-default mx-10"
          @click="goBack"
        >
          <i class="icon md-chevron-left" aria-hidden="true"></i>
          Back
        </button>
      </div>
    </div>
    <div class="page-content page-content-table">
      <div class="container-fluid">
        <!-- Campaign Info Header -->
        <div class="row" v-if="info">
          <div class="col-md-12">
            <div class="card">
              <div class="card-body">
                <div class="row">
                  <div class="col-md-2">
                    <strong>Campaign ID:</strong>
                    {{ campaign.id }}
                  </div>
                  <div class="col-md-2">
                    <strong>No. WS:</strong> {{ info.count_ws }}
                  </div>
                  <div class="col-md-2">
                    <strong>No. GT:</strong> {{ info.count_gt }}
                  </div>
                  <div class="col-md-2">
                    <strong>Status:</strong>
                    <span>
                      {{ info.status.toUpperCase() }}
                    </span>
                  </div>
                  <div class="col-md-4">
                    <strong>Last Update:</strong>
                    {{ formatDate(info.last_upload) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Data Table Panel -->
        <div class="panel">
          <div class="panel-body p-0">
            <!-- Action Buttons -->
            <div class="row p-4">
              <div class="col-md-12">
                <div class="d-flex justify-content-between">
                  <div>
                    <button
                      type="button"
                      class="btn btn-primary"
                      @click="showImportModal = true"
                      :disabled="isUploading"
                    >
                      <i v-if="isUploading" class="fa fa-spinner fa-spin"></i>
                      {{ isUploading ? "Processing..." : "Import Progress" }}
                    </button>
                    <a
                      href="#"
                      class="btn btn-link"
                      @click.prevent="downloadExample"
                    >
                      Click to download example
                    </a>
                  </div>
                  <div>
                    <button type="button" class="btn" @click="exportToExcel">
                      <i class="fa fa-file-excel-o"></i>
                      Export to Excel
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <vue-good-table
              v-if="datatable && columns.length > 0"
              ref="table"
              mode="remote"
              :columns="columns"
              :rows="datatable.data"
              :totalRows="datatable.total_records"
              :isLoading="isUploading"
              :pagination-options="{
                enabled: true,
                mode: 'records',
                perPage: 10,
                perPageDropdown: [10, 20, 50, 100],
                dropdownAllowAll: false,
                setCurrentPage: 1,
                nextLabel: 'Next',
                prevLabel: 'Prev',
                rowsPerPageLabel: 'Rows per page',
                ofLabel: 'of',
                allLabel: 'All',
              }"
              :search-options="{
                enabled: true,
                placeholder: 'Search...',
                trigger: 'enter',
              }"
              :sort-options="{
                enabled: true,
                initialSortBy: { field: 'ws_isms_code', type: 'asc' },
              }"
              @on-search="onSearch"
              @on-sort-change="onSortChange"
              @on-page-change="onPageChange"
              @on-per-page-change="onPerPageChange"
              styleClass="vgt-table striped"
            >
              <template slot="table-row" slot-scope="props">
                <span v-if="props.column.field === 'gt_shop_tel'">
                  {{
                    formatPhoneNumber(props.formattedRow[props.column.field])
                  }}
                </span>
                <span v-else-if="props.column.field.startsWith('month')">
                  {{ props.formattedRow[props.column.field] || "-" }}
                </span>
                <span v-else>
                  {{ props.formattedRow[props.column.field] || "-" }}
                </span>
              </template>
            </vue-good-table>

            <div
              v-else-if="!datatable || isUploading"
              class="text-center py-50"
            >
              <loader></loader>
              <p v-if="isUploading" class="mt-3">
                Processing uploaded data... Please wait.
              </p>
            </div>

            <div v-else class="text-center py-50">
              <p>No data available</p>
            </div>
          </div>
        </div>

        <!-- Import Modal -->
        <div
          v-if="showImportModal"
          class="modal fade show"
          style="display: block; background-color: rgba(0, 0, 0, 0.5)"
        >
          <div class="modal-dialog">
            <div class="modal-content">
              <div class="modal-header">
                <h4 class="modal-title">Import Progress</h4>
                <button type="button" class="close" @click="closeImportModal">
                  <span>&times;</span>
                </button>
              </div>
              <div class="modal-body">
                <div class="alert alert-warning">
                  <h5>This will overwrite existing data.</h5>
                  <p>Before committing, must check:</p>
                  <ul>
                    <li>No duplicate WS in many ONGOING campaigns</li>
                    <li>No duplicate GT-WS in the same file.</li>
                    <li>Validate data for each column</li>
                  </ul>
                </div>
                <file-upload
                  accept=".xlsx,.xls"
                  :max-size="10485760"
                  @change="onFileChange"
                  @remove="onFileRemove"
                ></file-upload>
              </div>
              <div class="modal-footer">
                <button
                  type="button"
                  class="btn btn-default"
                  @click="closeImportModal"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  class="btn btn-primary"
                  @click="importProgress"
                  :disabled="!importFile"
                >
                  Import
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import { wsgtdriveService } from "@/api/client";
import Loader from "@/components/shared/Loader.vue";
import vueGoodTableMixins from "@/mixins/vue-good-table";
import FileUpload from "./components/FileUpload.vue";

export default {
  name: "WSGTDriveProgressUpdate",

  mixins: [vueGoodTableMixins],

  components: {
    FileUpload,
    Loader,
  },

  data() {
    return {
      campaign: null,
      info: null,
      datatable: null,
      columns: [],
      showImportModal: false,
      importFile: null,
      isUploading: false,
      uploadCheckInterval: null,
    };
  },

  computed: {
    dynamicColumns() {
      if (!this.info || !this.info.month_list) return [];

      return this.info.month_list.map((month, index) => ({
        label: month,
        field: `month${index + 1}`,
        sortable: true,
        width: "80px",
      }));
    },
  },

  watch: {
    async "$route.query"() {
      if (this.campaign) {
        await this.getDataTable();
      }
    },
  },

  methods: {
    goBack() {
      this.$router
        .push({
          name: "WSGTDriveEdit",
          params: { id: this.campaign.id },
        })
        .catch({});
    },

    async getInfo() {
      const id = this.campaign.id;
      this.info = await wsgtdriveService.getUploadDataInfo(id);
    },

    async getDataTable(params = {}) {
      const id = this.campaign.id;

      // Get search and pagination parameters from route query or passed params
      const queryParams = {
        search: this.$route.query.search || params.search || "",
        start: this.$route.query.start || params.start || 0,
        count: this.$route.query.count || params.count || 10,
        desc: this.$route.query.desc || params.desc || 0,
      };

      // Only add order_by if it has a valid value
      const orderBy = this.$route.query.order_by || params.order_by;
      if (orderBy && orderBy.trim() !== "") {
        queryParams.order_by = orderBy;
      }

      this.datatable = await wsgtdriveService.getUploadDataTable(
        id,
        queryParams
      );
    },

    async fetchCampaignInfo() {
      const id = this.$route.params.id;
      this.campaign = await wsgtdriveService.getById(id);
    },

    setupColumns() {
      const baseColumns = [
        {
          label: "WS ISMS",
          field: "ws_isms_code",
          sortable: true,
          width: "120px",
        },
        {
          label: "GT Name",
          field: "gt_shop_name",
          sortable: true,
          width: "150px",
        },
        {
          label: "Tel. No",
          field: "gt_shop_tel",
          sortable: true,
          width: "180px",
        },
        {
          label: "GT Address",
          field: "gt_shop_addr",
          sortable: true,
          width: "400px",
        },
        {
          label: "Reward (bht)",
          field: "reward",
          sortable: true,
          width: "140px",
        },
        {
          label: "Status",
          field: "status",
          sortable: true,
          width: "140px",
        },
      ];

      // Add dynamic month columns
      const monthColumns = this.dynamicColumns;

      this.columns = [...baseColumns, ...monthColumns];
    },

    formatDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      return `${date.getDate().toString().padStart(2, "0")}/${(
        date.getMonth() + 1
      )
        .toString()
        .padStart(2, "0")}/${date.getFullYear()} ${date
        .getHours()
        .toString()
        .padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")} น.`;
    },

    formatPhoneNumber(phone) {
      if (!phone) return "-";
      // Remove any non-digit characters
      const digits = phone.replace(/\D/g, "");
      // Format as xxx-xxx-xxxx if 10 digits, otherwise return as-is
      if (digits.length === 10) {
        return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
      }
      return phone;
    },

    downloadExample() {
      // Create example data based on the image provided
      const exampleData = [
        {
          "WS ISMS": "1111111",
          "GT Name": "ร้านA",
          "GT Tel No": "123456789",
          "GT Address": "",
          Reward: 5,
          Oct: "5/5",
          Nov: "1/5",
          Dec: "2/5",
        },
        {
          "WS ISMS": "2222222",
          "GT Name": "ร้านB",
          "GT Tel No": "0123456788",
          "GT Address": "",
          Reward: 10,
          Oct: "0/5",
          Nov: "0/5",
          Dec: "5/5",
        },
      ];

      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(exampleData);

      // Set column widths
      const columnWidths = [
        { wch: 15 }, // WS ISMS
        { wch: 20 }, // GT Name
        { wch: 15 }, // GT Tel No
        { wch: 25 }, // GT Address
        { wch: 10 }, // Reward
        { wch: 10 }, // Oct
        { wch: 10 }, // Nov
        { wch: 10 }, // Dec
      ];
      worksheet["!cols"] = columnWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, "Progress Data");

      // Generate filename with current date
      const now = new Date();
      const dateStr = `${now.getFullYear()}${(now.getMonth() + 1)
        .toString()
        .padStart(2, "0")}${now.getDate().toString().padStart(2, "0")}`;
      const filename = `progress_upload_example_${dateStr}.xlsx`;

      // Download the file
      XLSX.writeFile(workbook, filename);
    },

    async importProgress() {
      if (!this.importFile) return;

      try {
        this.isUploading = true;

        // Create FormData for file upload
        const formData = new FormData();
        formData.append("excel", this.importFile); // Use 'excel' as field name per API spec

        // Upload using the existing API method
        await wsgtdriveService.uploadDataUpload(this.campaign.id, formData);

        this.$notify("File uploaded successfully. Processing data...");

        // Start checking upload status
        this.checkUploadStatus();

        this.showImportModal = false;
        this.importFile = null;
      } catch (error) {
        console.error("Import error:", error.response.data);
        if (error.response) {
          this.$notify({
            type: "error",
            title: "Failed to import progress data",
            text: error.response.data,
            duration: -1,
          });
        } else {
          this.$notify({
            type: "error",
            title: "Failed to import progress data",
            text: "An unexpected error occurred",
            duration: -1,
          });
        }
        this.isUploading = false;
      }
    },

    async checkUploadStatus() {
      try {
        const status = await wsgtdriveService.getUploadDataStatus(
          this.campaign.id
        );

        if (status.upload_in_progress === 1) {
          // Still uploading, check again after 2 seconds
          this.uploadCheckInterval = setTimeout(() => {
            this.checkUploadStatus();
          }, 2000);
        } else {
          // Upload completed, refresh data
          this.isUploading = false;
          await Promise.all([this.getInfo(), this.getDataTable()]);
          this.setupColumns();
          this.$notify("Progress data imported and processed successfully");

          // Clear any existing interval
          if (this.uploadCheckInterval) {
            clearTimeout(this.uploadCheckInterval);
            this.uploadCheckInterval = null;
          }
        }
      } catch (error) {
        console.error("Upload status check error:", error);
        this.isUploading = false;
        this.$notify("Failed to check upload status");
      }
    },

    async exportToExcel() {
      try {
        this.$notify("Preparing Excel export...");

        // Get ALL data for export (no pagination)
        const exportParams = {
          search: "",
          start: 0,
          count: 100000000, // Get all records
          order_by: "ws_isms_code", // Use valid column name for export
          desc: 0,
        };

        const allDataTable = await wsgtdriveService.getUploadDataTable(
          this.campaign.id,
          exportParams
        );

        if (
          !allDataTable ||
          !allDataTable.data ||
          allDataTable.data.length === 0
        ) {
          this.$notify("No data available to export");
          return;
        }

        // Prepare data for export in the same format as import
        const exportData = allDataTable.data.map((row) => {
          const exportRow = {};

          // Map the data using the exact same format as import template
          exportRow["WS ISMS"] = row.ws_isms_code || "";
          exportRow["GT Name"] = row.gt_shop_name || "";
          exportRow["GT Tel No"] = row.gt_shop_tel || "";
          exportRow["GT Address"] = row.gt_shop_addr || "";
          exportRow["Reward"] = row.reward; // Add if this field exists in your data
          exportRow["Status"] = row.status; // Add if this field exists in your data

          // Add month columns dynamically based on info.month_list
          if (this.info && this.info.month_list) {
            this.info.month_list.forEach((month, index) => {
              const monthField = `month${index + 1}`;
              exportRow[month] = row[monthField] || "";
            });
          }

          return exportRow;
        });

        // Create workbook and worksheet
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(exportData);

        // Set column widths
        const columnWidths = [
          { wch: 15 }, // WS ISMS
          { wch: 20 }, // GT Name
          { wch: 15 }, // GT Tel No
          { wch: 30 }, // GT Address
          { wch: 10 }, // Reward
        ];

        // Add widths for month columns
        if (this.info && this.info.month_list) {
          this.info.month_list.forEach(() => {
            columnWidths.push({ wch: 12 });
          });
        }

        worksheet["!cols"] = columnWidths;

        // Add worksheet to workbook
        XLSX.utils.book_append_sheet(workbook, worksheet, "Progress Data");

        // Generate filename with campaign name and date
        const now = new Date();
        const dateStr = `${now.getFullYear()}${(now.getMonth() + 1)
          .toString()
          .padStart(2, "0")}${now.getDate().toString().padStart(2, "0")}`;
        const campaignName = this.campaign?.name || "campaign";
        const sanitizedCampaignName = campaignName
          .replace(/[^\w\s-]/g, "")
          .replace(/\s+/g, "_");
        const filename = `${sanitizedCampaignName}_progress_${dateStr}.xlsx`;

        // Download the file
        XLSX.writeFile(workbook, filename);

        this.$notify("Data exported to Excel successfully");
      } catch (error) {
        console.error("Export error:", error);
        this.$notify("Failed to export data to Excel");
      }
    },

    onFileChange(event) {
      const file = event.target.files[0];
      this.importFile = file;
    },

    onFileRemove() {
      this.importFile = null;
    },

    closeImportModal() {
      // Clear any existing notifications when closing modal
      this.$notify({
        clean: true,
      });

      this.showImportModal = false;
    },

    // Vue Good Table event handlers
    onSearch(params) {
      this.$router
        .push({
          query: Object.assign({}, this.$route.query, {
            search: params.searchTerm,
            start: 0, // Reset to first page on search
          }),
        })
        .catch(() => {});
    },

    onSortChange(params) {
      const sortParam = params[0] || {};
      this.$router
        .push({
          query: Object.assign({}, this.$route.query, {
            order_by: sortParam.field,
            desc: sortParam.type === "desc" ? 1 : 0,
            start: 0, // Reset to first page on sort
          }),
        })
        .catch(() => {});
    },

    onPageChange(params) {
      const start = (params.currentPage - 1) * params.currentPerPage;
      this.$router
        .push({
          query: Object.assign({}, this.$route.query, {
            start: start,
            count: params.currentPerPage,
          }),
        })
        .catch(() => {});
    },

    onPerPageChange(params) {
      this.$router
        .push({
          query: Object.assign({}, this.$route.query, {
            start: 0, // Reset to first page
            count: params.currentPerPage,
          }),
        })
        .catch(() => {});
    },
  },

  async created() {
    await this.fetchCampaignInfo();

    await Promise.all([this.getInfo(), this.getDataTable()]);

    // Setup columns after data is loaded
    this.setupColumns();
  },

  beforeDestroy() {
    // Clear upload check interval if it exists
    if (this.uploadCheckInterval) {
      clearTimeout(this.uploadCheckInterval);
      this.uploadCheckInterval = null;
    }
  },
};
</script>

<style scoped>
.mb-30 {
  margin-bottom: 30px;
}

.mb-20 {
  margin-bottom: 20px;
}

.py-50 {
  padding: 50px 0;
}

.card {
  border: 1px solid #e3e6f0;
  border-radius: 5px;
}

.card-body {
  padding: 1.5rem;
}

.badge {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1;
  text-align: center;
  white-space: nowrap;
  vertical-align: baseline;
  border-radius: 0.25rem;
}

.badge-warning {
  color: #fff;
  background-color: #f6c23e;
}

.badge-success {
  color: #fff;
  background-color: #1cc88a;
}

.badge-info {
  color: #fff;
  background-color: #36b9cc;
}

.badge-secondary {
  color: #fff;
  background-color: #858796;
}

.modal {
  position: fixed;
  top: 0;
  left: 0;
  /* z-index: 1050; */
  width: 100%;
  height: 100%;
  overflow: hidden;
  outline: 0;
}

.modal-dialog {
  position: relative;
  width: auto;
  margin: 1.75rem auto;
  max-width: 500px;
}

.modal-content {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 0.3rem;
  box-shadow: 0 0.25rem 0.5rem rgba(0, 0, 0, 0.5);
}

.modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.modal-title {
  margin: 0;
  line-height: 1.5;
}

.modal-body {
  position: relative;
  flex: 1 1 auto;
  padding: 1rem;
}

.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 1rem;
  border-top: 1px solid #e9ecef;
}

.modal-footer .btn + .btn {
  margin-left: 0.5rem;
}

.close {
  background: transparent;
  border: 0;
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1;
  color: #000;
  opacity: 0.5;
  cursor: pointer;
  padding: 0.25rem;
  margin: -0.25rem -0.25rem -0.25rem auto;
}

.close:hover {
  opacity: 0.75;
}

.alert {
  position: relative;
  padding: 0.75rem 1.25rem;
  margin-bottom: 1rem;
  border: 1px solid transparent;
  border-radius: 0.25rem;
}

.alert-warning {
  color: #856404;
  background-color: #fff3cd;
  border-color: #ffeaa7;
}

.d-flex {
  display: flex !important;
}

.justify-content-between {
  justify-content: space-between !important;
}

.text-center {
  text-align: center !important;
}

.mt-3 {
  margin-top: 1rem !important;
}

.btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.fa-spin {
  animation: fa-spin 2s infinite linear;
}

@keyframes fa-spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(359deg);
  }
}
</style>

<style>
table.vgt-table tr {
  cursor: default;
}
</style>
